import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { PROJECTS_SORT_BY_MAP } from "@components/projects/projects-sort-constants";
import { getSortByFromGridSortModel } from "@components/table/projects/projects-table-utils";
import { useProjectsTableColumns } from "@components/table/projects/use-projects-table-columns";
import { useProjectsTableContext } from "@components/table/projects/use-projects-table-context";
import { PaginatedProjects } from "@custom-types/project-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import {
  GridColumnOrderChangeParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { ProjectsPageBulkActions } from "@pages/projects/projects-page-bulk-actions";
import { API_PROJECTS_PER_REQUEST } from "@store/projects/projects-slice-utils";
import {
  ProjectEvents,
  TableEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import { swapColumnOrder } from "@context-providers/table/column-preferences-utils";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { ProjectsSort } from "@components/projects/projects-sort";

type Props = Pick<
  PaginatedProjects,
  | "companyId"
  | "projects"
  | "groupId"
  | "projectArchivingState"
  | "nextPageCursor"
  | "loadMoreProjects"
  | "sortBy"
  | "setSortBy"
> & {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;
};

/**
 * Listing all the projects of a subject such as a company or a group in a table
 */
export function ProjectsTable({
  companyId,
  projects,
  isLoading,
  groupId,
  projectArchivingState,
  nextPageCursor,
  loadMoreProjects,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const { navigateToProjectDetail } = useAppNavigation();
  const { trackEvent } = useTrackEvent();
  const contextType = useProjectsTableContext({
    projectArchivingState,
    groupId,
  });

  const { columnPreferences, visibleOrderedColumns, updateColumnPreferences } =
    useColumnPreferencesContext();

  const columns = useProjectsTableColumns({
    companyId,
    contextType,
  });
  // ##### Server side pagination, searching and sorting logic ##### //

  const sortModel = [PROJECTS_SORT_BY_MAP[sortBy]];

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      // Track the sort event
      trackEvent({
        name: TableEvents.sortItems,
        props: {
          subject: "Project",
          sortBy: PROJECTS_SORT_BY_MAP[newSortBy].field,
          sortOrder: PROJECTS_SORT_BY_MAP[newSortBy].sort,
          viewMode: "list",
        },
      });

      setSortBy(newSortBy);
    },
    [setSortBy, trackEvent]
  );

  // Callback fired when a project row is clicked
  const handleRowClick = useCallback(
    (projectId: string) => {
      trackEvent({
        name: ProjectEvents.openProjectDetails,
        props: {
          projectId,
          eventLocation: contextType,
          viewMode: "list",
        },
      });
      navigateToProjectDetail({
        companyId,
        projectId,
      });
    },
    [trackEvent, contextType, navigateToProjectDetail, companyId]
  );

  // Callback to load more projects when navigating forward
  const handleLoadMoreProjects = useCallback(async () => {
    return await loadMoreProjects();
  }, [loadMoreProjects]);

  function handleColumnOrderChange(params: GridColumnOrderChangeParams): void {
    if (!columnPreferences) {
      return;
    }

    const { oldIndex, targetIndex } = params;

    if (oldIndex !== targetIndex) {
      const updatedPreferences = swapColumnOrder(
        columnPreferences,
        visibleOrderedColumns,
        oldIndex,
        targetIndex
      );

      void updateColumnPreferences(updatedPreferences);
    }
  }

  // ##### Bulk actions logic ##### //

  // The available bulk actions to display according to the archiving status and table context.
  const actionButtons = ProjectsPageBulkActions({
    companyId,
    projects,
    projectStatus: projectArchivingState,
    subjectType: contextType === "group projects" ? "group" : "workspace",
  });

  // Map context type to TableType enum for FaroTable
  let tableTypeEnum: TableType;
  if (contextType === "active projects") {
    tableTypeEnum = TableType.activeProjects;
  } else if (contextType === "archived projects") {
    tableTypeEnum = TableType.archiveProjects;
  } else {
    tableTypeEnum = TableType.groupProjects;
  }

  return (
    <FaroTable
      entities={projects}
      uniqueIdKey="id"
      isLoading={isLoading}
      columns={columns}
      onRowClick={handleRowClick}
      rowHeight={70}
      bulkActionButtons={actionButtons}
      pageSize={API_PROJECTS_PER_REQUEST}
      onNextPage={handleLoadMoreProjects}
      hasMoreItems={!!nextPageCursor}
      tableType={tableTypeEnum}
      defaultSortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      paginationMode="server"
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={PROJECTS_TABLE_COLUMN_LABELS}
          disabledColumns={["name"]}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
      sortActions={
        <ProjectsSort
          key="projects-sort"
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      }
      sx={{
        // Change cursor to pointer when hovering over table rows
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
